// src/utils/dateFormatter.js
//
// Locale-aware date formatting and date computation for label
// elements. Used by the canvas renderer for live preview and by the
// Preview dialog's date picker.
//
// PHASE 2 OF DATE UNIFICATION (2026-06-13):
//
// The server (lib/dateFormatter.js) was updated to support the new
// unified date model with dateMode='print'/'produced'/'expiry'/'fixed'.
// This file mirrors that logic so canvas preview matches what
// PDF/ZPL will print.
//
// Legacy fieldType='date_plus' and 'value_only(date)' rows still
// flow through here — resolveDateMode() derives the right mode for
// them so they render unchanged until the user re-saves them through
// the new UI.

const FORMATS = {
  da: { date: 'dd-MM-yyyy', time: 'HH:mm', sep: ' ' },
  en: { date: 'dd/MM/yyyy', time: 'HH:mm', sep: ' ' },
  sv: { date: 'yyyy.MM.dd', time: 'HH:mm', sep: ' ' },
  no: { date: 'dd.MM.yyyy', time: 'HH:mm', sep: ' ' },
  fi: { date: 'dd.MM.yyyy', time: 'HH:mm', sep: ' ' },
  de: { date: 'dd.MM.yyyy', time: 'HH:mm', sep: ' ' },
  ja: { date: 'yyyy/MM/dd', time: 'HH:mm', sep: ' ' },
}

// Patterns use Unicode/ICU tokens (yyyy, yy, MM, dd, HH, mm) — the same
// syntax Dart's DateFormat takes natively, so a pattern stored in
// designer_elements.dateFormat is fed straight to DateFormat(pattern)
// on the tablet with no translation layer to drift out of sync.
//
// The table above used to be DD-MM-YYYY. Changing the case is not
// cosmetic: MM (month) and mm (minute) differ ONLY by case, so a
// case-insensitive scheme cannot express both.
const TOKEN_RE = /yyyy|yy|MM|dd|HH|mm/g

/**
 * Render a Date through a pattern.
 *
 * One regex pass, not chained .replace() calls. The old code did
 * .replace('DD',D).replace('MM',M).replace('YYYY',Y), which works only
 * as long as no substituted value can itself contain a later token —
 * true for digits, but it breaks the moment a pattern gains a literal.
 * A single pass over the tokens has no such ordering hazard, and it
 * also removes the yyyy-before-yy trap.
 */
export function applyPattern(date, pattern) {
  return String(pattern).replace(TOKEN_RE, (token) => {
    switch (token) {
      case 'yyyy': return String(date.getFullYear())
      case 'yy':   return pad(date.getFullYear() % 100)
      case 'MM':   return pad(date.getMonth() + 1)
      case 'dd':   return pad(date.getDate())
      case 'HH':   return pad(date.getHours())
      case 'mm':   return pad(date.getMinutes())
      default:     return token
    }
  })
}

function pad(n) {
  return String(n).padStart(2, '0')
}

/**
 * Parse a date-like value into a Date in local time.
 *
 *   - Date instance         → returned as-is
 *   - "YYYY-MM-DDTHH:mm:ss" → parsed as local time (no TZ shift)
 *   - "YYYY-MM-DD"          → parsed as local midnight
 */
function parseDate(input) {
  if (input instanceof Date) return input
  if (typeof input !== 'string') return new Date(NaN)
  if (/^\d{4}-\d{2}-\d{2}$/.test(input)) {
    return new Date(`${input}T00:00:00`)
  }
  return new Date(input)
}

/**
 * Serialize a Date to a local-time ISO string WITHOUT timezone suffix:
 *   "2026-05-21T14:32:00"
 */
export function toLocalIso(date) {
  if (!(date instanceof Date) || isNaN(date.getTime())) return ''
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
}

export function parseLocalIso(str) {
  if (!str) return null
  const d = parseDate(str)
  return isNaN(d.getTime()) ? null : d
}

/**
 * Format a date.
 *
 * [pattern] overrides the locale's date pattern — this is
 * designer_elements.dateFormat, set per element in the properties
 * panel. Null/empty means "follow the locale", which stays the default
 * so existing rows are unaffected.
 *
 * The pattern governs the DATE part only; showTime still appends the
 * locale's time pattern. Keeping those separate means the existing
 * showTime checkbox continues to mean one thing, rather than competing
 * with time tokens embedded in a custom pattern.
 */
export function formatDate(input, locale = 'en', showTime = false, pattern = null) {
  const date = parseDate(input)
  if (isNaN(date.getTime())) return ''
  const fmt = FORMATS[locale] || FORMATS.en
  const datePart = applyPattern(date, pattern || fmt.date)
  if (!showTime) return datePart
  return `${datePart}${fmt.sep}${applyPattern(date, fmt.time)}`
}

// Map daysOffsetField → token that holds the precomputed end-date.
// When foodItems API supplies this we use it directly rather than
// recomputing base + offset, so renderer and backend agree.
const OFFSET_FIELD_TO_TOKEN = {
  thawing:          'thawing_date',
  thawingDays:      'thawing_date',
  thawingHours:     'thawing_date',
  refrigerated:     'use_by_date',
  refrigeratedDays: 'use_by_date',
  refrigeratedHours:'use_by_date',
  expiry:           'expiry_date',
  expiryDays:       'expiry_date',
  expiryHours:      'expiry_date',
}

const DATE_BASE_TO_TOKEN = {
  produced: 'production_date',
  opened:   'opened_date',
}

// Derive the effective dateMode. New rows have it explicitly;
// legacy rows get derived from fieldType + valueOnlyType.
function resolveDateMode(el) {
  if (el.dateMode) return el.dateMode
  if (el.fieldType === 'date_plus') return 'expiry'
  if (el.fieldType === 'value_only') {
    if (el.valueOnlyType === 'date_plus') return 'expiry'
    return 'produced'
  }
  return 'produced'
}

// Truncate a computed expiry DOWN to the nearest quarter hour.
//
// Mirrors _roundExpiryDown in the tablet's HomeController. Driven from
// the data payload rather than an options argument, because
// renderDateValue is called from three renderers that all already have
// `data` and none of which know about customer settings:
//
//   data.roundExpiryDownToQuarter  the customer setting
//   data.offsetUnit               the item's 'days' | 'hours'
//
// DOWN, never up: rounding up prints an expiry later than the rule
// allows — a 3-hour item printed at 13:07 would read 16:15, eight
// minutes past its own limit.
//
// Both keys must be present. Absent offsetUnit means the payload
// predates this, and rounding a day-based shelf life could move the
// DATE back for anything computed just after midnight — so the safe
// default is to leave the value alone.
function roundExpiryDown(date, data = {}) {
  if (!data.roundExpiryDownToQuarter) return date
  if (data.offsetUnit !== 'hours') return date
  if (isNaN(date.getTime())) return date

  const out = new Date(date)
  out.setMinutes(out.getMinutes() - (out.getMinutes() % 15), 0, 0)
  return out
}

export function computeDate(el, data = {}) {
  const mode = resolveDateMode(el)

  // Print mode — wall clock (or pre-set print_date for batch consistency)
  if (mode === 'print') {
    if (data.print_date) {
      const d = parseDate(data.print_date)
      if (!isNaN(d.getTime())) return d
    }
    return new Date()
  }

  // Fixed mode — hardcoded date stored on the element
  if (mode === 'fixed') {
    if (el.fixedDate) {
      const d = parseDate(el.fixedDate)
      if (!isNaN(d.getTime())) return d
    }
    return new Date(NaN)
  }

  // Expiry mode — base + offset OR precomputed end-date token
  if (mode === 'expiry') {
    // Path A: foodItems API already computed it
    const endToken = OFFSET_FIELD_TO_TOKEN[el.daysOffsetField]
    if (endToken && data[endToken]) {
      const d = parseDate(data[endToken])
      if (!isNaN(d.getTime())) return roundExpiryDown(d, data)
    }

    // Path B: compute it ourselves
    const baseToken = DATE_BASE_TO_TOKEN[el.dateBase] || 'production_date'
    const baseValue = data[baseToken]
    const base = baseValue ? parseDate(baseValue) : new Date()
    if (isNaN(base.getTime())) return new Date(NaN)

    const offsetDays = Number(data[el.daysOffsetField])
      || Number(el.daysOffset)
      || 0
    if (offsetDays) base.setDate(base.getDate() + offsetDays)
    return roundExpiryDown(base, data)
  }

  // Produced mode — the base date itself
  const token = DATE_BASE_TO_TOKEN[el.dateBase] || 'production_date'
  if (data[token]) {
    const d = parseDate(data[token])
    if (!isNaN(d.getTime())) return d
  }

  // Fall back to now when the payload carries no production_date /
  // opened_date. Returning NaN here meant renderDateValue produced an
  // empty string, so a Produktionsdato or Åbningsdato element rendered
  // blank on the canvas and in the PDF — while the tablet printed
  // today's date, because its date controllers default to today.
  //
  // Note the expiry branch above already does exactly this ("const base
  // = baseValue ? parseDate(baseValue) : new Date()"). The two paths
  // simply disagreed, which is why expiry dates showed up and base
  // dates didn't.
  //
  // data.print_date is honoured first so a batch keeps one consistent
  // moment across its labels rather than drifting a second per page.
  if (data.print_date) {
    const d = parseDate(data.print_date)
    if (!isNaN(d.getTime())) return d
  }
  return new Date()
}

export function renderDateValue(el, data = {}, locale = 'en') {
  const d = computeDate(el, data)
  if (isNaN(d.getTime())) return ''
  return formatDate(d, locale, !!el.showTime, el.dateFormat || null)
}

export { resolveDateMode }

// The set of token names that represent dates — the Preview dialog
// uses this to decide whether to render a date picker vs text field.
export const DATE_TOKENS = new Set([
  'print_date', 'production_date', 'opened_date',
  'expiry_date', 'use_by_date', 'thawing_date',
])

// Presets offered in the designer's date-format dropdown. The column
// accepts any pattern built from the tokens above — these are just the
// ones worth one click.
//
// `value: null` means "follow the locale", which is the default and
// what every existing row has.
export const DATE_FORMAT_PRESETS = [
  { value: null,         sample: null },
  { value: 'dd-MM-yyyy', sample: '14-08-2026' },
  { value: 'dd/MM/yyyy', sample: '14/08/2026' },
  { value: 'dd.MM.yyyy', sample: '14.08.2026' },
  { value: 'yyyy-MM-dd', sample: '2026-08-14' },
  { value: 'yyyy.MM.dd', sample: '2026.08.14' },
  { value: 'yyyyMMdd',   sample: '20260814' },
  { value: 'dd-MM-yy',   sample: '14-08-26' },
  { value: 'dd/MM/yy',   sample: '14/08/26' },
]

/// The pattern a locale renders with when no dateFormat is set — used
/// to label the "follow the locale" option with what it will produce.
export function localeDatePattern(locale = 'en') {
  return (FORMATS[locale] || FORMATS.en).date
}